'use client';

import { createContext, useContext, useEffect, useRef, useState, ReactNode } from 'react';

interface DataFreshnessContextType {
  anchorDate: string | null; // the dataload/volume_sync anchor date, e.g. '2026-03-02'
  isLoading: boolean;
  isError: boolean;
}

const DataFreshnessContext = createContext<DataFreshnessContextType | null>(null);

export function DataFreshnessProvider({ children }: { children: ReactNode }) {
  const [anchorDate, setAnchorDate] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isError, setIsError] = useState(false);
  const hasFetched = useRef(false);

  useEffect(() => {
    if (hasFetched.current) return;
    hasFetched.current = true;

    (async () => {
      try {
        const res = await fetch('/api/data-freshness');
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        setAnchorDate(data.anchorDate ?? null);
        setIsError(false);
      } catch (e) {
        console.error('[DataFreshness] fetch error:', e);
        setIsError(true);
      } finally {
        setIsLoading(false);
      }
    })();
  }, []);

  return (
    <DataFreshnessContext.Provider value={{ anchorDate, isLoading, isError }}>
      {children}
    </DataFreshnessContext.Provider>
  );
}

export function useDataFreshness(): DataFreshnessContextType {
  const ctx = useContext(DataFreshnessContext);
  if (!ctx) throw new Error('useDataFreshness must be used within DataFreshnessProvider');
  return ctx;
}
